import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'

import {
    archiveCustomerDocument,
    getCustomerDocumentDownloadUrl,
    listCustomerDocuments,
    renameCustomerDocument,
    uploadCustomerDocument,
} from './api'
import {documentKeys} from './query-keys'
import type {RenameDocumentRequest} from './schemas'

export function useCustomerDocuments(customerId: string, options?: {enabled?: boolean}) {
    return useQuery({
        queryKey: documentKeys.list(customerId),
        queryFn: () => listCustomerDocuments(customerId),
        enabled: options?.enabled ?? Boolean(customerId),
    })
}

export function useCreateCustomerDocument(customerId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: ({name, file}: {name: string; file: File}) =>
            uploadCustomerDocument(customerId, name, file),
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: documentKeys.lists(customerId),
            })
        },
    })
}

export function useRenameCustomerDocument(customerId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: ({documentId, input}: {documentId: string; input: RenameDocumentRequest}) =>
            renameCustomerDocument(customerId, documentId, input),
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: documentKeys.lists(customerId),
            })
        },
    })
}

export function useArchiveCustomerDocument(customerId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (documentId: string) => archiveCustomerDocument(customerId, documentId),
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: documentKeys.lists(customerId),
            })
        },
    })
}

export {getCustomerDocumentDownloadUrl}
